import { REALTIME_EVENTS, type RealtimeEvent, type RealtimePayloads } from '@an-crm/shared';
import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { adminKeys } from '@/features/admin/api';
import { notificationKeys } from '@/features/notifications/api';
import { platformKeys } from '@/features/platform/api';
import { coreKeys } from '@/lib/core-keys';
import { meQuery, navigationQuery } from '../session/queries';

/**
 * Which cached queries each realtime event makes stale. Payloads are only hints: the API stays the
 * source of truth, so every event simply invalidates (prefix-matching) query keys.
 */
export const REALTIME_INVALIDATIONS: Readonly<Record<RealtimeEvent, readonly QueryKey[]>> = {
  [REALTIME_EVENTS.navigationChanged]: [meQuery.queryKey, navigationQuery.queryKey, adminKeys.permissions],
  [REALTIME_EVENTS.usersChanged]: [adminKeys.users, adminKeys.roles],
  [REALTIME_EVENTS.rolesChanged]: [adminKeys.roles, adminKeys.users],
  [REALTIME_EVENTS.notificationCreated]: [notificationKeys.all],
  [REALTIME_EVENTS.sessionRevoked]: [],
  [REALTIME_EVENTS.jobsChanged]: [platformKeys.jobs],
  [REALTIME_EVENTS.tenantsChanged]: [platformKeys.tenants],
  [REALTIME_EVENTS.pipelineChanged]: [
    coreKeys.pipeline,
    coreKeys.accounts,
    coreKeys.touches,
    coreKeys.targets,
  ],
  [REALTIME_EVENTS.playbookChanged]: [coreKeys.playbook],
  [REALTIME_EVENTS.libraryChanged]: [coreKeys.library],
  [REALTIME_EVENTS.importChanged]: [coreKeys.imports, coreKeys.accounts, coreKeys.pipeline],
};

/** The minimal socket surface we bind to (a Socket.IO client socket satisfies it). */
export interface RealtimeSocketLike {
  on(event: string, listener: (payload: never) => void): unknown;
}

export interface RealtimeDeps {
  queryClient: Pick<QueryClient, 'invalidateQueries'>;
  onSessionRevoked: (payload: RealtimePayloads['session.revoked']) => void;
  onNotification: (payload: RealtimePayloads['notification.created']) => void;
}

export function bindRealtimeHandlers(socket: RealtimeSocketLike, deps: RealtimeDeps): void {
  for (const event of Object.values(REALTIME_EVENTS)) {
    socket.on(event, (payload: unknown) => {
      for (const queryKey of REALTIME_INVALIDATIONS[event]) {
        void deps.queryClient.invalidateQueries({ queryKey });
      }
      if (event === REALTIME_EVENTS.sessionRevoked) {
        deps.onSessionRevoked(payload as RealtimePayloads['session.revoked']);
      } else if (event === REALTIME_EVENTS.notificationCreated) {
        deps.onNotification(payload as RealtimePayloads['notification.created']);
      }
    });
  }
}
